import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { useI18n } from '../i18n/index.tsx';
import './ui.css';

/**
 * The small set of building blocks the screens are made of.
 *
 * Deliberately few and deliberately plain: a customer who does not use Linux
 * is helped by a consistent, quiet interface far more than by a rich one.
 */

/** Forwards its ref, so a confirmation can move focus onto its own button. */
export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    busy?: boolean;
    icon?: ReactNode;
  }
>(function Button(
  { variant = 'primary', size = 'md', busy, icon, children, className, ...rest },
  ref,
) {
  return (
    <button
      type="button"
      {...rest}
      ref={ref}
      // A busy button that stays clickable submits twice.
      disabled={rest.disabled || busy}
      aria-busy={busy || undefined}
      className={['btn', `btn--${variant}`, `btn--${size}`, className].filter(Boolean).join(' ')}
    >
      {busy ? <Spinner /> : icon}
      <span>{children}</span>
    </button>
  );
});

export function Spinner({ label }: { label?: string }): ReactNode {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Card({
  children,
  className,
  as: Element = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'article' | 'div';
}): ReactNode {
  return <Element className={['card', className].filter(Boolean).join(' ')}>{children}</Element>;
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <header className="card__header">
      <div className="card__heading">
        <h2>{title}</h2>
        {description ? <p className="muted">{description}</p> : null}
      </div>
      {actions ? <div className="card__actions">{actions}</div> : null}
    </header>
  );
}

export function Field({
  label,
  help,
  error,
  required,
  children,
}: {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}): ReactNode {
  const id = useId();
  const { t } = useI18n();
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={['field', error ? 'field--invalid' : ''].filter(Boolean).join(' ')}>
      <label htmlFor={id}>
        {label}
        {required ? null : <span className="field__optional"> · {t('common.optional')}</span>}
      </label>
      {children({ id, 'aria-describedby': describedBy })}
      {help ? (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Forwards its ref, so a screen can move focus to it.
 *
 * Needed by the restore confirmation, which must take focus when it appears
 * or a keyboard user is left at the top of the page with no idea that a
 * dialogue asking them to destroy their data has opened below.
 */
export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput(props, ref) {
    return (
      <input
        {...props}
        ref={ref}
        className={['input', props.className].filter(Boolean).join(' ')}
      />
    );
  },
);

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactNode {
  return (
    <textarea
      {...props}
      className={['input', 'textarea', props.className].filter(Boolean).join(' ')}
    />
  );
}

export function Checkbox({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  help?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  return (
    <div className="checkbox">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div>
        <label htmlFor={id}>{label}</label>
        {help ? <p className="field__help">{help}</p> : null}
      </div>
    </div>
  );
}

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}): ReactNode {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Notice({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <div className="notice__body">
        {title ? <strong>{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {actions ? <div className="notice__actions">{actions}</div> : null}
    </div>
  );
}

/**
 * Asking before doing something that cannot be undone.
 *
 * This replaced `window.confirm`, which is four things a customer-facing
 * product cannot have:
 *
 *  - its buttons are in the *browser's* language. A Persian customer reading
 *    a Persian question was answering it with "OK" and "Cancel";
 *  - a browser or an embedded view with dialogs suppressed returns `false`,
 *    so the destructive action silently does nothing and the customer is
 *    given no reason. A failure rendered as "nothing happened" is the same
 *    defect this project keeps finding in a different costume;
 *  - it blocks the page, including the event stream that draws installation
 *    progress;
 *  - it cannot say which server, which chat, or what exactly will happen, and
 *    cannot be exercised by a test.
 *
 * Focus moves into it when it opens, for the reason the restore confirmation
 * already documents: the button that had focus is usually the one that just
 * disappeared. Escape cancels, because a question a keyboard user cannot
 * dismiss is a trap.
 */
export function Confirm({
  title,
  children,
  confirmLabel,
  tone = 'danger',
  busy,
  onConfirm,
  onCancel,
}: {
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: ReactNode;
  tone?: 'danger' | 'warning';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const { t } = useI18n();
  const confirm = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirm.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <Notice
      tone={tone}
      title={title}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button ref={confirm} variant="danger" busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Notice>
  );
}

/**
 * A choice between a handful of options, as cards rather than a dropdown.
 *
 * The wizard's choices are the decisions of the whole product; a `<select>`
 * hides them behind a click and gives no room to explain what each one means.
 */
export function ChoiceGroup<T extends string>({
  legend,
  help,
  value,
  options,
  onChange,
  columns = 2,
}: {
  legend: ReactNode;
  help?: ReactNode;
  value: T | undefined;
  options: readonly {
    readonly value: T;
    readonly title: ReactNode;
    readonly description?: ReactNode;
    readonly meta?: ReactNode;
    readonly disabled?: boolean;
    readonly disabledReason?: ReactNode;
    readonly recommended?: boolean;
  }[];
  onChange: (value: T) => void;
  columns?: 1 | 2 | 3;
}): ReactNode {
  const name = useId();
  return (
    <fieldset className="choice">
      <legend>{legend}</legend>
      {help ? <p className="muted choice__help">{help}</p> : null}
      <div className="choice__grid" data-columns={columns}>
        {options.map((option) => (
          <label
            key={option.value}
            className={[
              'choice__option',
              value === option.value ? 'is-selected' : '',
              option.disabled ? 'is-disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
            />
            <span className="choice__content">
              <span className="choice__title">
                {option.title}
                {option.recommended ? <Badge tone="accent">★</Badge> : null}
              </span>
              {option.description ? (
                <span className="choice__description">{option.description}</span>
              ) : null}
              {option.meta ? <span className="choice__meta">{option.meta}</span> : null}
              {option.disabled && option.disabledReason ? (
                <span className="choice__disabled-reason">{option.disabledReason}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** A value the customer will need to copy, such as an address or a password. */
export function CopyableValue({
  value,
  secret,
  label,
}: {
  value: string;
  secret?: boolean;
  label?: string;
}): ReactNode {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState(!secret);
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is on screen either way.
      setRevealed(true);
    }
  };

  return (
    <div className="copyable">
      <span className="copyable__value mono" aria-label={label}>
        {revealed ? value : '•'.repeat(Math.min(value.length, 24))}
      </span>
      <div className="copyable__actions">
        {secret ? (
          <Button size="sm" variant="ghost" onClick={() => setRevealed((shown) => !shown)}>
            {revealed ? t('common.hide') : t('common.show')}
          </Button>
        ) : null}
        <Button size="sm" variant="secondary" onClick={() => void copy()}>
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="empty">
      <div className="empty__mark" aria-hidden="true" />
      <h3>{title}</h3>
      {description ? <p className="muted">{description}</p> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }): ReactNode {
  const { t } = useI18n();
  return (
    <div className="skeleton" role="status" aria-label={t('common.loading')}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="skeleton__line" />
      ))}
    </div>
  );
}
